import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Scales as Scale, CircleNotch as Loader2, Warning } from '@phosphor-icons/react';
import { fetchSession, type JourneyIntent, type NextState } from '@/lib/authJourney';
import { useAuthStore } from '@/features/auth/store';
import { routeForMember } from '@/features/auth/hooks';
import { fetchPreferences } from '@/lib/meApi';
import { PREFERENCES_QUERY_KEY } from '@/lib/preferences';
import { useQueryClient } from '@tanstack/react-query';

/**
 * /post-login — the Session Bootstrap surface (docs/04-ENGINEERING/02-API-CONTRACTS.md §10.1).
 *
 * The OAuth callback authenticates only and lands here. This page runs the Bootstrap state machine
 * (Loading Session → Loading Workspace → render the resolver's decision) and RENDERS whatever
 * `nextState` the resolver returns. It never computes workspace resolution itself.
 */

type Stage = 'session' | 'workspace' | 'done';

const STAGE_COPY: Record<Stage, string> = {
  session: 'Signing you in…',
  workspace: 'Resolving workspace…',
  done: 'Almost there…',
};

// Non-READY states render an inline status card. READY navigates to the dashboard.
// CHOOSER/PROVISION/INVITE get their own screens in Steps 3–4; until then they explain themselves
// rather than dead-ending — the contract is complete; the screens are filled in progressively.
const STATE_COPY: Record<Exclude<NextState, 'READY'>, { title: string; body: string }> = {
  CHOOSER: { title: 'Choose a workspace', body: 'You belong to more than one workspace. The workspace chooser arrives in the next step.' },
  PROVISION: { title: 'Let’s create your workspace', body: 'Creating a new workspace is part of onboarding, coming in the next milestone.' },
  INVITE: { title: 'Accept your invitation', body: 'You were invited to a chambers. The invite-acceptance flow arrives in the next step.' },
  SUSPENDED: { title: 'Workspace suspended', body: 'This workspace is currently suspended. Please contact your chambers administrator.' },
  ACCESS_DENIED: { title: 'No workspace to enter', body: 'Your account isn’t a member of any workspace yet. Create a workspace or ask to be invited.' },
  ERROR: { title: 'Something went wrong', body: 'We couldn’t resolve your session. Please sign in again.' },
  NOT_IMPLEMENTED: { title: 'Coming soon', body: 'This part of the journey isn’t available yet.' },
};

/**
 * Where this person starts.
 *
 * `appearance.landingPage` is PERSONAL: it decides where THIS user opens, and nothing else. It
 * is not a workspace setting — a clerk choosing the diary does not move a barrister's starting
 * page — and it grants nothing. If the stored path is not one this member type may enter,
 * `ProtectedRoute` sends them to their role's home, so a stale preference degrades to the
 * default instead of dead-ending. The server has already refused anything that is not an
 * in-app path, which is what stops this becoming an open redirect.
 */
async function entryPointFor(memberType: string, adopt: (prefs: unknown) => void): Promise<string> {
  const roleHome = routeForMember(memberType as Parameters<typeof routeForMember>[0]);
  try {
    const prefs = await fetchPreferences();
    adopt(prefs);
    return prefs.landingPage ?? roleHome;
  } catch {
    // Signing in must never depend on a preference being readable.
    return roleHome;
  }
}

export default function PostLoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [stage, setStage] = useState<Stage>('session');
  const [terminal, setTerminal] = useState<Exclude<NextState, 'READY'> | null>(null);

  useEffect(() => {
    const intent = (params.get('intent') as JourneyIntent | null) ?? 'sign_in';
    let cancelled = false;

    (async () => {
      try {
        setStage('workspace');
        const session = await fetchSession(intent); // the 401-interceptor refreshes the token from the cookie
        if (cancelled) return;

        if (session.nextState === 'READY') {
          setStage('done');
          // The refresh during fetchSession populated the auth store with the member type.
          const memberType = useAuthStore.getState().user?.memberType;
          // Seed the shared preferences cache while we are here, so the shell paints with the
          // person's density and formats the first dates correctly rather than with defaults.
          const target = memberType
            ? await entryPointFor(memberType, (prefs) => qc.setQueryData(PREFERENCES_QUERY_KEY, prefs))
            : '/';
          if (cancelled) return;
          navigate(target, { replace: true });
          return;
        }
        setTerminal(session.nextState);
      } catch {
        if (!cancelled) setTerminal('ERROR');
      }
    })();

    return () => { cancelled = true; };
  }, [params, navigate, qc]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm text-center">
        <div className="mb-6 flex justify-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-brand-600 text-white">
            <Scale className="h-5 w-5" weight="duotone" />
          </div>
        </div>

        {terminal === null ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-brand-600 dark:text-brand-400" />
            <div className="text-sm font-medium text-foreground">{STAGE_COPY[stage]}</div>
            <div className="text-xs text-muted-foreground">Setting up your workspace</div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300">
              <Warning className="h-5 w-5" />
            </div>
            <div className="text-base font-semibold text-foreground">{STATE_COPY[terminal].title}</div>
            <p className="text-sm text-muted-foreground">{STATE_COPY[terminal].body}</p>
            <Link
              to="/login"
              className="mt-2 inline-flex h-10 items-center justify-center rounded-md bg-brand-600 px-5 text-sm font-medium text-white transition hover:bg-brand-700"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
